import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

export const GameContext = createContext(null);

const defaultGameContext = {
  game: null,
  loading: false,
  error: null,
  loadGame: async () => {},
  setGame: () => {},
  generateStory: async () => {},
  makeChoice: async () => {},
  travel: async () => {},
  equipItem: async () => {},
  unequipItem: async () => {},
  upgradeCard: async () => {},
  useItem: async () => {},
  getSphereGrid: async () => ({}),
  unlockSphereNode: async () => ({}),
  respecSphereGrid: async () => ({}),
  refreshGame: async () => {},
  battleAction: async () => {},
  endBattle: async () => {},
};

export const useGame = () => {
  const ctx = useContext(GameContext);
  return ctx || defaultGameContext;
};

// Retry wrapper for transient backend errors (network, 500, 502, 503, 504).
// 402 (AI budget), 404, 400, 401, 403, 429 are NOT retried.
// onRetry(attempt, totalAttempts) is called BEFORE each retry (not before the
// first attempt) so the UI can display an "inchiostro vacilla..." hint.
async function withRetry(fn, { attempts = 3, baseDelayMs = 700, onRetry = null } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const retriable = !status || status >= 500 || e?.code === "ECONNABORTED" || e?.code === "ERR_NETWORK";
      if (!retriable || i === attempts - 1) throw e;
      const delay = baseDelayMs * Math.pow(2, i); // 700ms, 1400ms, 2800ms
      try { onRetry && onRetry(i + 1, attempts); } catch { /* noop */ }
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

const formatErrorDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || (typeof d === "string" ? d : JSON.stringify(d))).join(", ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return fallback;
};

export const GameProvider = ({ children }) => {
  const { token, API } = useAuth();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadGame = useCallback(async (gameId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await withRetry(
        () => axios.get(`${API}/game/${gameId}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 })
      );
      setGame(res.data);
      if (gameId) localStorage.setItem("ac_last_game_id", gameId);
      return res.data;

    } catch (e) {
      setError(formatErrorDetail(e, "Failed to load game"));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const createGame = useCallback(async (characterName, characterClass, setting) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/game/new`, {
        character_name: characterName,
        character_class: characterClass,
        setting: setting
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data);
      return res.data;
    } catch (e) {
      setError(formatErrorDetail(e, "Failed to create game"));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const generateStory = useCallback(async (gameId, onRetry = null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await withRetry(
        () => axios.post(`${API}/game/${gameId}/story/generate`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 240000 }),
        { attempts: 2, baseDelayMs: 1500, onRetry }
      );
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      setError(formatErrorDetail(e, "Failed to generate story"));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const makeChoice = useCallback(async (gameId, choiceText, choiceId, onRetry = null, parentPageNumber = null) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        choice_text: choiceText,
        choice_id: choiceId
      };
      if (parentPageNumber !== null && parentPageNumber !== undefined) {
        payload.parent_page_number = parentPageNumber;
      }
      const res = await withRetry(
        () => axios.post(`${API}/game/${gameId}/story/choice`, payload, { headers: { Authorization: `Bearer ${token}` }, timeout: 240000 }),
        { attempts: 2, baseDelayMs: 1500, onRetry }
      );
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      setError(formatErrorDetail(e, "Failed to make choice"));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const battleAction = useCallback(async (gameId, action, cardId, allyTargets, targetId) => {
    setLoading(true);
    try {
      const payload = { action, card_id: cardId };
      if (allyTargets && typeof allyTargets === "object" && Object.keys(allyTargets).length > 0) {
        payload.ally_targets = allyTargets;
      }
      // Iter 106 — explicit target id for "heal ally" actions
      if (targetId) payload.target = targetId;
      const res = await axios.post(`${API}/game/${gameId}/battle/action`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      setError(formatErrorDetail(e, "Battle action failed"));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const endBattle = useCallback(async (gameId) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/battle/end`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to end battle");
      throw e;
    }
  }, [API, token]);

  const equipItem = useCallback(async (gameId, cardId, slot) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/inventory/equip`, {
        card_id: cardId, slot
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to equip");
      throw e;
    }
  }, [API, token]);

  const unequipItem = useCallback(async (gameId, cardId, slot) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/inventory/unequip`, {
        card_id: cardId, slot
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to unequip");
      throw e;
    }
  }, [API, token]);

  const generateMapImage = useCallback(async (gameId) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/map/generate-image`, {}, { headers: { Authorization: `Bearer ${token}` } });
      // Iter 97 — propagate the new map_url to local state so the <img>
      // re-renders immediately. Without this the player had to reload the
      // page before the freshly generated map appeared.
      const newUrl = res.data?.image_url;
      if (newUrl) {
        setGame(prev => prev ? ({
          ...prev,
          map_data: { ...(prev.map_data || {}), image_url: newUrl },
        }) : prev);
      }
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || "Map generation failed");
      throw e;
    }
  }, [API, token]);

  const travel = useCallback(async (gameId, locationName, onRetry = null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await withRetry(
        () => axios.post(`${API}/game/${gameId}/map/travel`, {
          location_name: locationName
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 240000 }),
        { attempts: 2, baseDelayMs: 1500, onRetry }
      );
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(d => d.msg || (typeof d === "string" ? d : JSON.stringify(d))).join(", ") 
          : (detail ? JSON.stringify(detail) : "Failed to travel");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const upgradeCard = useCallback(async (gameId, cardId, cardCategory) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/card/upgrade`, {
        card_id: cardId, card_category: cardCategory
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [API, token]);

  const useItem = useCallback(async (gameId, cardId, cardCategory) => {
    try {
      const res = await axios.post(`${API}/game/${gameId}/item/use`, {
        card_id: cardId, card_category: cardCategory
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data.game);
      return res.data;
    } catch (e) {
      throw e;
    }
  }, [API, token]);

  const allocateStat = useCallback(async (gameId, stat) => {
    const res = await axios.post(`${API}/game/${gameId}/allocate-stat`, { stat }, { headers: { Authorization: `Bearer ${token}` } });
    setGame(res.data.game);
    return res.data;
  }, [API, token]);

  const evolveClass = useCallback(async (gameId, evolutionName) => {
    const res = await axios.post(`${API}/game/${gameId}/evolve-class`, { evolution_name: evolutionName }, { headers: { Authorization: `Bearer ${token}` } });
    setGame(res.data.game);
    return res.data;
  }, [API, token]);

  const getEvolutions = useCallback(async (gameId) => {
    const res = await axios.get(`${API}/game/${gameId}/class-evolutions`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  }, [API, token]);

  const getSphereGrid = useCallback(async (gameId) => {
    const res = await axios.get(`${API}/game/${gameId}/sphere-grid`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  }, [API, token]);

  const unlockSphereNode = useCallback(async (gameId, nodeId) => {
    const res = await axios.post(`${API}/game/${gameId}/sphere-grid/unlock-node`, { node_id: nodeId }, { headers: { Authorization: `Bearer ${token}` } });
    if (res.data.game) setGame(res.data.game);
    return res.data;
  }, [API, token]);

  const respecSphereGrid = useCallback(async (gameId) => {
    const res = await axios.post(`${API}/game/${gameId}/sphere-grid/respec`, {}, { headers: { Authorization: `Bearer ${token}` } });
    if (res.data.game) setGame(res.data.game);
    return res.data;
  }, [API, token]);

  return (
    <GameContext.Provider value={{
      game, setGame, loading, error, setError,
      loadGame, createGame, generateStory, makeChoice,
      battleAction, endBattle, equipItem, unequipItem, generateMapImage, travel, upgradeCard, useItem,
      allocateStat, evolveClass, getEvolutions,
      getSphereGrid, unlockSphereNode, respecSphereGrid
    }}>
      {children}
    </GameContext.Provider>
  );
};
